import {
  Visibility,
  VisibilityOff,
  Cancel,
  CheckCircle,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { ResetPassword } from "../../../hooks/resetPassword";
import { useLoading } from "../../../components/Loading/useLoading";
import { useSnackbar } from "notistack";

const REQUIREMENTS = [
  {
    key: "minLength",
    label: "Mínimo 8 caracteres",
    test: (v) => v.length >= 8,
  },
  {
    key: "uppercase",
    label: "Al menos una letra mayúscula",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    key: "lowercase",
    label: "Al menos una letra minúscula",
    test: (v) => /[a-z]/.test(v),
  },
  { key: "number", label: "Al menos un número", test: (v) => /[0-9]/.test(v) },
  {
    key: "symbol",
    label: "Al menos un símbolo: . _ @ ( )",
    test: (v) => /[._@()]/.test(v),
  },
  {
    key: "noSpaces",
    label: "Sin espacios en blanco",
    test: (v) => v.length > 0 && !/\s/.test(v),
  },
];
export const NewPassword = ({ username, handleClose }) => {
  const { setAppLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const [newPassword, setNewPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [touched, setTouched] = useState({ new: false, confirm: false });
  const { resetPasswordMutation } = ResetPassword();

  const allValid = REQUIREMENTS.every((r) => r.test(newPassword));
  const passwordsMatch =
    newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allValid && passwordsMatch;

  const savePassword = (body) => {
    setAppLoading(true);
    resetPasswordMutation(body, {
      onSuccess: (data) => {
        enqueueSnackbar(
          "La contraseña se actualizó con éxito, ahora puede iniciar sesión nuevamente",
          {
            autoHideDuration: 3000,
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          "No se pudo actualizar la contraseña, intente mas tarde",
          {
            autoHideDuration: 2000,
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
          },
        );
      },
      onSettled: () => {
        handleClose();
        setAppLoading(false);
      },
    });
  };

  const handleSubmit = () => {
    setTouched({ new: true, confirm: true });
    if (canSubmit) {
      const payload = {
        username,
        newPassword,
      };
      savePassword(payload);
    }
  };

  const confirmSuccess = confirmPassword.length > 0 && passwordsMatch;
  const confirmError =
    touched.confirm && confirmPassword.length > 0 && !passwordsMatch;

  return (
    <Grid container spacing={0}>
      <Grid item xs={12} sx={{ p: 0, mb: 2, pr: 1 }} className="center_item">
        <p style={{ textAlign: "justify" }}>
          Ingrese su nueva contraseña, asegurese de cumplir con los parámetros
          establecidos y que ambos campos coincidad
        </p>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          p: 0,
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          mb: 0,
        }}
      >
        <TextField
          id="newPassword"
          name="newPassword"
          label="Nueva contraseña"
          type={showNew ? "text" : "password"}
          size="small"
          fullWidth
          onChange={(e) => {
            setNewPassword(e.target.value);
            setTouched((t) => ({ ...t, new: true }));
          }}
          sx={{ mb: 1.5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNew(!showNew)}
                  edge="end"
                  sx={{ color: "rgba(255,255,255,0.4)" }}
                >
                  {showNew ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          p: 0,
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          mb: 2,
        }}
      >
        <Collapse in={touched.new || newPassword.length > 0}>
          <Box
            sx={{
              mb: 2,
              p: 0,
            }}
          >
            <List dense disablePadding>
              {REQUIREMENTS.map((req) => {
                const ok = req.test(newPassword);
                return (
                  <ListItem key={req.key} disablePadding sx={{ py: -1.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {ok ? (
                        <CheckCircle sx={{ fontSize: 16, color: "#575E3B" }} />
                      ) : (
                        <Cancel sx={{ fontSize: 16, color: "#FF1744" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={req.label}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: {
                          color: ok ? "#575E3B" : "rgba(255,23,68,0.75)",
                          transition: "color 0.25s ease",
                          fontSize: "11px",
                        },
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Collapse>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          p: 0,
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          label="Confirmar contraseña"
          type={showConfirm ? "text" : "password"}
          value={confirmPassword}
          size="small"
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setTouched((t) => ({ ...t, confirm: true }));
          }}
          error={confirmError}
          sx={{
            mb: 0.75,
            "& .MuiOutlinedInput-root": {
              ...(confirmSuccess && {
                "& fieldset": { borderColor: "#575E3B !important" },
              }),
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirm(!showConfirm)}
                  edge="end"
                  sx={{ color: "rgba(255,255,255,0.4)" }}
                >
                  {showConfirm ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {confirmError && (
        <Grid
          item
          xs={12}
          sx={{
            p: 0,
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              width: "100%",
              mb: 1,
              p: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            Las contraseñas no coinciden
          </Alert>
        </Grid>
      )}
      {confirmSuccess && (
        <Grid
          item
          xs={12}
          sx={{
            p: 0,
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              width: "100%",
              mb: 1,
              p: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            Las contraseñas coinciden
          </Alert>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        sx={{
          p: 0,
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          className="btn_lg"
          type="button"
          onClick={handleSubmit}
        >
          Guardar
        </Button>
      </Grid>
    </Grid>
  );
};
